/**
 * System Updates API Client
 * Fetches system updates/changelog for login notifications and update banners.
 */

import { ApiClient } from '../../../core/apiClient';

export interface SystemUpdate {
  id: number;
  title: string;
  summary: string;
  details: string | null;
  category: 'ai_capability' | 'feature' | 'bugfix' | 'security' | 'performance' | 'maintenance' | 'announcement';
  severity: 'info' | 'important' | 'critical';
  version: string | null;
  show_on_login: boolean;
  dismissed: boolean;
  created_at: string;
}

export interface UnseenUpdatesResponse {
  success: boolean;
  unseen_count: number;
  updates: SystemUpdate[];
}

export interface ReleaseWithSections extends SystemUpdate {
  ai_status: 'raw' | 'needs_review' | 'reviewed';
  commit_hash: string | null;
  base_commit_hash: string | null;
  published_at: string | null;
  sections: Record<string, string> | null;
}

export interface ListUpdatesResponse {
  success: boolean;
  total: number;
  updates: SystemUpdate[];
}

export interface UpdateCountResponse {
  success: boolean;
  unseen_count: number;
}

class SystemUpdatesApi extends ApiClient {
  constructor() {
    super({ enableLogging: false });
  }

  /**
   * Get unseen updates for login notification popup
   */
  async getUnseenUpdates(): Promise<UnseenUpdatesResponse> {
    try {
      const response = await this.client.get<UnseenUpdatesResponse>('/system-updates/unseen');
      return response.data;
    } catch (error) {
      console.warn('[SystemUpdates] Failed to fetch unseen updates:', error);
      return { success: false, unseen_count: 0, updates: [] };
    }
  }

  /**
   * Get count of unseen updates (lightweight for badge)
   */
  async getUnseenCount(): Promise<number> {
    try {
      const response = await this.client.get<UpdateCountResponse>('/system-updates/count');
      return response.data?.unseen_count || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Dismiss a specific update
   */
  async dismissUpdate(updateId: number): Promise<void> {
    try {
      await this.client.post(`/system-updates/${updateId}/dismiss`, {});
    } catch (error) {
      console.warn('[SystemUpdates] Failed to dismiss update:', error);
    }
  }

  /**
   * Dismiss all updates
   */
  async dismissAllUpdates(): Promise<void> {
    try {
      await this.client.post('/system-updates/dismiss-all', {});
    } catch (error) {
      console.warn('[SystemUpdates] Failed to dismiss all updates:', error);
    }
  }

  /**
   * List all releases for the /changelog page (paginated).
   */
  async listReleases(limit = 20, offset = 0): Promise<ListUpdatesResponse> {
    try {
      const response = await this.client.get<ListUpdatesResponse>(
        `/system-updates?limit=${limit}&offset=${offset}`,
      );
      return response.data;
    } catch {
      return { success: false, total: 0, updates: [] };
    }
  }

  /**
   * Fetch a single release with its per-persona sections decoded.
   */
  async getRelease(id: number): Promise<ReleaseWithSections | null> {
    try {
      const response = await this.client.get<{ success: boolean; release: ReleaseWithSections }>(
        `/system-updates/${id}/full`,
      );
      return response.data?.release || null;
    } catch {
      return null;
    }
  }

  /**
   * System-admin edit path for AI-generated release notes.
   */
  async updateRelease(id: number, patch: Partial<{
    title: string;
    summary: string;
    category: SystemUpdate['category'];
    severity: SystemUpdate['severity'];
    sections: Record<string, string>;
    markReviewed: boolean;
  }>): Promise<boolean> {
    try {
      await this.client.patch(`/system-updates/${id}`, patch);
      return true;
    } catch (error) {
      console.warn('[SystemUpdates] Failed to update release:', error);
      return false;
    }
  }
}

export const systemUpdatesApi = new SystemUpdatesApi();
